import { format } from 'date-fns'
import { ArrowUpRight, Loader2, AlertCircle } from 'lucide-react'
import React, { useEffect, useState, useContext, useCallback } from 'react'
import { toast } from 'sonner'
import axiosInstance from '../../../api/axiosInstance'
import logger from '../../../utils/logger'
import PlanCard from './PlanCard'
import { AuthContext } from '../../../contexts/auth-context'

import {
  cancelSubscriptionService,
  createCustomerPortalSessionService,
  getBusinessSubscriptionService,
  reactivateSubscriptionService,
  createCheckoutSessionService,
  getAllPlansService,
  createCustomerHomePortalSessionService,
} from '../../../services'
import { Button } from '../../ui/button'
import { Switch } from '../../ui/switch'
import { Label } from '../../ui/label'

import PaymentDisclaimerModal from './PaymentDisclaimerModal'

// Generic Confirmation Modal Component (Keep as is)
const ConfirmationModal = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText,
  cancelText,
  isConfirming,
}) => {
  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-50 p-4 font-inter"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-auto relative transform transition-all duration-300 scale-100 opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">{title}</h3>
        <p className="text-gray-700 text-lg text-center mb-6">{message}</p>

        <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={onCancel}
            className="flex-1 sm:flex-none px-6 py-3 text-lg border-gray-300 text-gray-700 hover:bg-gray-100 rounded-md shadow-sm"
            disabled={isConfirming}
          >
            {cancelText || 'Cancel'}
          </Button>
          <Button
            onClick={onConfirm}
            className="flex-1 sm:flex-none bg-heading-primary hover:bg-[#643d73] text-white font-semibold px-6 py-3 text-lg rounded-md shadow-md"
            disabled={isConfirming}
          >
            {isConfirming ? <Loader2 className="animate-spin h-5 w-5 mr-2" /> : null}
            {confirmText || 'Confirm'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function BusinessPlansComponent() {
  const { auth } = useContext(AuthContext)

  const [subscriptions, setSubscriptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [reactivateLoading, setReactivateLoading] = useState(false)
  const [activateLoading, setActivateLoading] = useState(false)

  // State to hold the fetched raw plans
  const [rawPlans, setRawPlans] = useState([])
  const [loadingPlans, setLoadingPlans] = useState(true)

  // Billing period toggle: false for monthly, true for yearly
  const [isYearlyBilling, setIsYearlyBilling] = useState(false)

  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false)
  // selectedPlanForUpgrade will now store the full plan object
  const [selectedPlanForUpgrade, setSelectedPlanForUpgrade] = useState(null)

  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false)
  const [showReactivateConfirmModal, setShowReactivateConfirmModal] = useState(false)
  const [showActivateConfirmModal, setShowActivateConfirmModal] = useState(false)

  const [upgradeStatus, setUpgradeStatus] = useState('idle')
  const [upgradeMessage, setUpgradeMessage] = useState('')

  // Derived state: Find the currently active subscription to display as the "Current Plan"
  const activeSubscription = subscriptions.find((sub) => sub.status === 'active')

  // Enriched activeSubscription: Finds the correct price (monthly/yearly)
  // based on the activeSubscription's priceId from the rawPlans
  const activeSubscriptionWithPrice = React.useMemo(() => {
    if (!activeSubscription || !rawPlans.length) return null

    let foundPrice = null
    let periodType = null // 'monthly' or 'yearly'

    for (const plan of rawPlans) {
      if (plan.price.monthly && plan.price.monthly.priceId === activeSubscription.priceId) {
        foundPrice = plan.price.monthly
        periodType = 'monthly'
        break
      }
      if (plan.price.yearly && plan.price.yearly.priceId === activeSubscription.priceId) {
        foundPrice = plan.price.yearly
        periodType = 'yearly'
        break
      }
    }

    if (foundPrice) {
      return {
        ...activeSubscription,
        actualPriceDetails: foundPrice, // Store the full price object (amount, currency, priceId)
        periodType: periodType, // 'monthly' or 'yearly'
        price: foundPrice.amount, // Simplified price for comparison, using amount directly
      }
    }
    return null
  }, [activeSubscription, rawPlans])

  // Use useCallback for fetchSubscriptionsAndPlans
  const fetchSubscriptionsAndPlans = useCallback(async () => {
    setLoading(true)
    setLoadingPlans(true) // Set loading for plans specifically

    try {
      // Determine the businessId to send to the backend.
      // It should only be sent if the user is authenticated.
      const businessId = auth?.authenticate ? auth.user?._id : null

      // --- FOR TESTING PURPOSES ONLY ---
      // Uncomment and set an IP to simulate a different location
      // const simulateIp = '49.37.161.0'; // Example IP for India (Jaipur is in India, so this simulates a non-local IP)
      // const simulateIp = '66.249.64.0'; // Example IP for USA (Google DNS)
      // --- END TESTING ---

      // Step A: Fetch ALL plans from your backend, passing businessId if logged in
      // and optionally a testIp for development
      const planResponse = await getAllPlansService(businessId /*, simulateIp */) // Pass businessId and optionally simulateIp

      if (planResponse) {
        setRawPlans(planResponse) // Store raw plans
      } else {
        setRawPlans([])
      }

      // ONLY fetch subscriptions if the user is authenticated
      if (auth?.authenticate) {
        const subResponse = await getBusinessSubscriptionService()
        if (subResponse.status === 200 && Array.isArray(subResponse.data.data)) {
          setSubscriptions(subResponse.data.data)
        } else {
          setSubscriptions([])
        }
      } else {
        // If not authenticated, clear subscriptions to avoid stale data
        setSubscriptions([])
      }
    } catch (e) {
      logger.error('Error fetching data:', e)
      if (!e.response || (e.response.status !== 401 && e.response.status !== 403)) {
        logger.info('No active subscription of the user or other network issue.')
      }
      setSubscriptions([])
      // TODO  -> yha se reset rawPlans to an empty array if needed
    } finally {
      setLoading(false)
      setLoadingPlans(false) // Always turn off plan loading
    }
  }, [auth?.authenticate, auth?.user?._id]) // Added auth.user._id as a dependency

  useEffect(() => {
    fetchSubscriptionsAndPlans()
  }, [fetchSubscriptionsAndPlans])

  const handleCancel = () => {
    if (!activeSubscription || activeSubscription.cancelAtPeriodEnd) {
      toast.info('This subscription cannot be canceled or is already set to cancel.')
      return
    }

    // Trial period validation
    const now = new Date()
    const trialStart = activeSubscription.trialStart
      ? new Date(activeSubscription.trialStart)
      : null
    const trialEnd = activeSubscription.trialEnd ? new Date(activeSubscription.trialEnd) : null
    const isInTrial = trialStart && trialEnd && now >= trialStart && now <= trialEnd

    if (isInTrial) {
      toast.warning(
        'Cancelling during your trial will immediately end your access and forfeit any remaining trial days. You will not be charged.'
      )
    }
    setShowCancelConfirmModal(true)
  }

  const confirmCancel = async () => {
    if (!activeSubscription) {
      toast.error('No active subscription found to cancel.')
      setShowCancelConfirmModal(false)
      return
    }
    try {
      setCancelLoading(true)
      const res = await cancelSubscriptionService(activeSubscription.stripeSubscriptionId)
      if (res?.status === 200) {
        toast.success('Subscription set to cancel at the end of the current billing period.')
        await fetchSubscriptionsAndPlans()
        // A slight delay to ensure UI updates or allow backend processing to complete
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      } else {
        toast.error(res?.data?.message || 'Failed to cancel subscription.')
      }
    } catch (error) {
      logger.error('Cancel error:', error)
      toast.error('Something went wrong during cancellation.')
    } finally {
      setCancelLoading(false)
      setShowCancelConfirmModal(false)
    }
  }

  const handleReactivate = () => {
    if (!activeSubscription || !activeSubscription.cancelAtPeriodEnd) {
      toast.info('This subscription is not pending cancellation and cannot be reactivated.')
      return
    }
    setShowReactivateConfirmModal(true)
  }

  const confirmReactivate = async () => {
    if (!activeSubscription) {
      toast.error('No subscription found to reactivate.')
      setShowReactivateConfirmModal(false)
      return
    }
    try {
      setReactivateLoading(true)
      const res = await reactivateSubscriptionService(activeSubscription.stripeSubscriptionId)
      if (res?.status === 200) {
        toast.success('Subscription reactivated successfully!')
        await fetchSubscriptionsAndPlans()
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      } else {
        toast.error(res?.data?.message || 'Failed to reactivate subscription.')
      }
    } catch (error) {
      logger.error('Reactivate error:', error)
      toast.error('Something went wrong during reactivation.')
    } finally {
      setReactivateLoading(false)
      setShowReactivateConfirmModal(false)
    }
  }

  const handleManageStripeCustomerPortal = async () => {
    try {
      setLoading(true) // General loading indicator for portal redirect
      const returnUrl = window.location.origin + '/business/profile/plans'
      const successPageUrl = window.location.origin + '/business/profile/plans'
      // Use the Stripe Customer ID from the active subscription if available,
      // otherwise fallback to the first one found (if any exist)

      const { url } = await createCustomerHomePortalSessionService(returnUrl, successPageUrl)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (error) {
      logger.error('Error redirecting to Stripe portal:', error.message)
      toast.error('Something went wrong while redirecting to the portal.')
    } finally {
      setLoading(false)
    }
  }

  const handleActivate = () => {
    const incompleteSub = subscriptions.find((sub) => sub.status === 'incomplete')
    if (!incompleteSub) {
      toast.info('No incomplete subscription to activate.')
      return
    }
    setShowActivateConfirmModal(true)
  }

  const confirmActivate = async () => {
    const incompleteSub = subscriptions.find((sub) => sub.status === 'incomplete')
    const successPageUrl = window.location.origin + '/business/profile/plans'
    if (!incompleteSub) {
      toast.error('No incomplete subscription found to activate.')
      setShowActivateConfirmModal(false)
      return
    }
    try {
      setActivateLoading(true)
      const returnUrl = window.location.origin + '/business/profile/plans'
      const { url } = await createCustomerPortalSessionService(returnUrl, successPageUrl)
      window.location.href = url
    } catch (error) {
      logger.error('Activate error:', error.message)
      toast.error('Something went wrong while activating the subscription.')
    } finally {
      setActivateLoading(false)
      setShowActivateConfirmModal(false)
    }
  }

  // MODIFIED handleUpgrade logic
  const handleUpgrade = (plan) => {
    if (!auth?.authenticate) {
      toast.error('Please log in to manage your plans.')
      return
    }
    setSelectedPlanForUpgrade(plan)
    setShowDisclaimerModal(true)
  }

  const initiateStripeUpgrade = async () => {
    if (!selectedPlanForUpgrade) return

    setShowDisclaimerModal(false)
    setUpgradeStatus('loading')
    setUpgradeMessage('Initiating plan change process...')

    try {
      const selectedPriceDetails = isYearlyBilling
        ? selectedPlanForUpgrade.price.yearly
        : selectedPlanForUpgrade.price.monthly

      if (!selectedPriceDetails || !selectedPriceDetails.priceId) {
        throw new Error('Selected plan does not have a valid price for the chosen billing period.')
      }

      const selectedPlanAmount = selectedPriceDetails.amount
      const selectedPlanPriceId = selectedPriceDetails.priceId
      const currentPlanAmount = activeSubscriptionWithPrice?.price || 0
      const currentPlanPriceId = activeSubscriptionWithPrice?.priceId // Get current priceId for exact match check

      const isDowngrade = activeSubscriptionWithPrice && selectedPlanAmount < currentPlanAmount
      const isUpgrade = activeSubscriptionWithPrice && selectedPlanAmount > currentPlanAmount
      const isSamePlanAndBillingPeriod =
        activeSubscriptionWithPrice &&
        selectedPlanAmount === currentPlanAmount &&
        currentPlanPriceId === selectedPriceDetails.priceId
      const isSamePlanDifferentPeriod =
        activeSubscriptionWithPrice &&
        selectedPlanAmount === currentPlanAmount &&
        currentPlanPriceId !== selectedPriceDetails.priceId
      const isNewPurchase = !activeSubscriptionWithPrice

      if (isSamePlanAndBillingPeriod) {
        toast.info('You are already on this exact plan and billing period.')
        setUpgradeStatus('idle')
        setSelectedPlanForUpgrade(null)
        return
      }
      // Handle switching between monthly/yearly on the SAME plan
      if (isSamePlanDifferentPeriod) {
        toast.info('Redirecting to update billing period...')
        // Use customer portal for billing period change
        const returnUrl = window.location.origin + '/business/profile/plans'
        const successPageUrl = window.location.origin + '/business/profile/plans'
        const { url } = await createCustomerPortalSessionService(returnUrl, successPageUrl)
        window.location.href = url
        setUpgradeStatus('success')
        setUpgradeMessage('Redirecting to update billing period...')
        return
      }

      if (isDowngrade) {
        toast.info('Redirecting to secure payment portal for downgrade confirmation...')
        const returnUrl = window.location.origin + '/business/profile/plans'
        const successPageUrl = window.location.origin + '/business/profile/plans'
        const { url } = await createCustomerPortalSessionService(
          returnUrl,
          successPageUrl,
          selectedPlanPriceId
        )
        window.location.href = url
        setUpgradeStatus('success')
        setUpgradeMessage('Redirecting to payment portal...')
      } else if (isUpgrade) {
        toast.info('Redirecting to secure payment portal for upgrade confirmation...')
        const returnUrl = window.location.origin + '/business/profile/plans'
        const successPageUrl = window.location.origin + '/business/profile/plans'
        const { url } = await createCustomerPortalSessionService(
          returnUrl,
          successPageUrl,
          selectedPlanPriceId
        )
        window.location.href = url
        setUpgradeStatus('success')
        setUpgradeMessage('Redirecting to payment portal...')
      } else if (isNewPurchase) {
        toast.info(
          `Redirecting to checkout for the ${selectedPlanForUpgrade.name} (${
            isYearlyBilling ? 'Yearly' : 'Monthly'
          }) plan...`
        )

        const payload = {
          priceId: selectedPriceDetails.priceId, // Use the specific monthly/yearly priceId
          successUrl: `${window.location.origin}/business/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: window.location.origin,
          mode: 'subscription',
        }
        const { url } = await createCheckoutSessionService(payload)
        if (!url) throw new Error('No checkout URL returned')
        window.location.href = url
        setUpgradeStatus('success')
        setUpgradeMessage('Redirecting to checkout...')
      }
    } catch (error) {
      logger.error('Plan change initiation error:', error)
      setUpgradeStatus('error')
      setUpgradeMessage(`Error: ${error.response?.data?.message || error.message}`)
      toast.error('Something went wrong while changing your plan.')
    } finally {
      setSelectedPlanForUpgrade(null)
      // Only set status to idle if it's an error, otherwise leave success/redirect status
      if (upgradeStatus === 'error') {
        setUpgradeStatus('idle')
      }
    }
  }

  // Adjusted loading checks for the main component's display
  // Use rawPlans for initial loading check
  if (loading && subscriptions.length === 0 && rawPlans.length === 0) {
    return (
      <div className="flex justify-center items-center py-20 min-h-screen">
        <Loader2 className="w-12 h-12 text-heading-primary animate-spin" />
        <p className="ml-4 text-lg text-gray-700">Loading subscription details and plans...</p>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto mt-10 px-4 font-inter">
      {upgradeStatus !== 'idle' && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-auto flex flex-col items-center justify-center">
            {upgradeStatus === 'loading' && (
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
            )}
            {upgradeStatus === 'success' && (
              <ArrowUpRight className="w-12 h-12 text-green-500 mb-4" />
            )}
            {upgradeStatus === 'error' && <AlertCircle className="w-12 h-12 text-red-500 mb-4" />}
            <p className="text-xl font-semibold text-gray-700 text-center">{upgradeMessage}</p>
            {upgradeStatus === 'error' && (
              <Button
                onClick={() => setUpgradeStatus('idle')}
                className="mt-6 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-6 py-3 text-lg rounded-md shadow-md"
              >
                Dismiss
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Conditional rendering for unauthenticated/no active subscription vs. authenticated with active sub */}
      {!auth?.authenticate || !activeSubscription ? (
        <div className="text-center text-gray-600 py-10 text-lg">
          <h2 className="mb-4 text-2xl font-semibold text-gray-800">
            {auth?.authenticate
              ? 'No active subscription found.'
              : 'Log in to view your current plan.'}
          </h2>
          <p className="mb-6">Explore our plans below to get started!</p>

          <div className="flex items-center justify-center space-x-2 mb-8">
            <Label htmlFor="billing-period-unauth" className="text-lg font-semibold text-gray-700">
              Monthly
            </Label>
            <Switch
              id="billing-period-unauth" // Unique ID for this switch
              checked={isYearlyBilling}
              onCheckedChange={setIsYearlyBilling}
              className="data-[state=checked]:bg-heading-primary"
            />
            <Label htmlFor="billing-period-unauth" className="text-lg font-semibold text-gray-700">
              Yearly
            </Label>
          </div>

          {loadingPlans ? (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-8 h-8 text-heading-primary animate-spin" />
              <p className="ml-2 text-gray-600">Loading plans...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {rawPlans.map((plan) => (
                <PlanCard
                  key={plan._id.$oid} // Assuming _id.$oid is how your _id string is accessed
                  plan={plan}
                  currentPlanTier={null}
                  currentPlanPrice={null} // No current price for new purchase
                  onSelectPlan={handleUpgrade}
                  isYearlyBilling={isYearlyBilling}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-800">📦 Current Subscription</h2>
            <Button
              onClick={handleManageStripeCustomerPortal}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium px-4 py-2 rounded-md transition-colors"
              variant="ghost"
              disabled={loading}
            >
              <span>Manage Subscription</span>
              <ArrowUpRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="bg-white shadow-md rounded-xl p-6 border border-gray-200">
            <div className="grid md:grid-cols-2 gap-4">
              <Info label="Plan Name" value={activeSubscription.planName} />
              <Info label="Tier" value={activeSubscription.planTier} />
              <Info
                label="Status"
                value={
                  activeSubscription.status.charAt(0).toUpperCase() +
                  activeSubscription.status.slice(1)
                }
                color={activeSubscription.status === 'active' ? 'text-green-600' : 'text-red-600'}
              />

              <Info label="Price ID" value={activeSubscription.priceId} />
              <Info
                label="Billing Period"
                value={
                  activeSubscriptionWithPrice?.periodType
                    ? activeSubscriptionWithPrice.periodType.charAt(0).toUpperCase() +
                      activeSubscriptionWithPrice.periodType.slice(1)
                    : 'N/A'
                }
              />
              <Info
                label="Current Price"
                value={
                  activeSubscriptionWithPrice?.actualPriceDetails
                    ? `${activeSubscriptionWithPrice.actualPriceDetails.currency}${(
                        activeSubscriptionWithPrice.actualPriceDetails.amount / 100
                      ).toFixed(2)}`
                    : 'N/A'
                }
              />

              <Info
                label="Plan Period Start"
                value={format(new Date(activeSubscription.currentPeriodStart), 'PPP')}
              />
              <Info
                label="Plan Period End"
                value={format(new Date(activeSubscription.currentPeriodEnd), 'PPP')}
              />
            </div>

            {activeSubscription.cancelAtPeriodEnd && (
              <p className="mt-4 text-red-600 font-medium p-2 bg-red-50 rounded-md border border-red-200">
                🚫 Your subscription is set to cancel at the end of the current billing period.
              </p>
            )}

            <div className="flex flex-wrap gap-4 mt-6">
              {activeSubscription.status === 'active' && activeSubscription.cancelAtPeriodEnd && (
                <Button
                  onClick={handleReactivate}
                  disabled={reactivateLoading || loading}
                  className="bg-heading-primary hover:bg-[#643d73] text-white font-semibold py-2 px-4 rounded-md shadow-sm disabled:opacity-50"
                >
                  {reactivateLoading ? <Loader2 className="animate-spin h-5 w-5 mr-2" /> : null}
                  Reactivate Subscription
                </Button>
              )}

              {subscriptions.some((sub) => sub.status === 'incomplete') && (
                <Button
                  onClick={handleActivate}
                  disabled={activateLoading || loading}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-md shadow-sm disabled:opacity-50"
                >
                  {activateLoading ? <Loader2 className="animate-spin h-5 w-5 mr-2" /> : null}
                  Activate Subscription
                </Button>
              )}

              {activeSubscription.status === 'active' && !activeSubscription.cancelAtPeriodEnd && (
                <Button
                  onClick={handleCancel}
                  disabled={cancelLoading || loading}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-md shadow-sm disabled:opacity-50"
                >
                  {cancelLoading ? <Loader2 className="animate-spin h-5 w-5 mr-2" /> : null}
                  Cancel Subscription
                </Button>
              )}
            </div>
          </div>

          <div className="mt-10">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">All Available Plans</h2>
            <div className="flex items-center justify-center space-x-2 mb-8">
              <Label htmlFor="billing-period-auth" className="text-lg font-semibold text-gray-700">
                Monthly
              </Label>
              <Switch
                id="billing-period-auth" // Unique ID for this switch
                checked={isYearlyBilling}
                onCheckedChange={setIsYearlyBilling}
                className="data-[state=checked]:bg-heading-primary"
              />
              <Label htmlFor="billing-period-auth" className="text-lg font-semibold text-gray-700">
                Yearly
              </Label>
            </div>
            {loadingPlans ? (
              <div className="flex justify-center items-center py-10">
                <Loader2 className="w-10 h-10 text-heading-primary animate-spin" />
                <p className="ml-2 text-gray-600">Loading plans...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rawPlans.map((plan) => (
                  <PlanCard
                    key={plan._id.$oid}
                    plan={plan}
                    currentPlanTier={activeSubscription?.planTier}
                    currentPlanPrice={activeSubscriptionWithPrice?.price}
                    onSelectPlan={handleUpgrade}
                    isYearlyBilling={isYearlyBilling}
                    activeSubscriptionPeriodType={activeSubscriptionWithPrice?.periodType}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {showDisclaimerModal && (
        <PaymentDisclaimerModal
          plan={selectedPlanForUpgrade}
          isYearlyBilling={isYearlyBilling}
          onConfirm={initiateStripeUpgrade}
          onClose={() => {
            setShowDisclaimerModal(false)
            setSelectedPlanForUpgrade(null)
          }}
        />
      )}

      <ConfirmationModal
        isOpen={showCancelConfirmModal}
        title="Confirm Cancellation"
        message="Are you sure you want to cancel your subscription at the end of the current billing period? You will retain access until then."
        onConfirm={confirmCancel}
        onCancel={() => setShowCancelConfirmModal(false)}
        confirmText="Yes, Cancel"
        cancelText="No, Keep It"
        isConfirming={cancelLoading}
      />
      <ConfirmationModal
        isOpen={showReactivateConfirmModal}
        title="Confirm Reactivation"
        message="Are you sure you want to reactivate your subscription? Your plan will resume immediately, and you will be billed on your next cycle date."
        onConfirm={confirmReactivate}
        onCancel={() => setShowReactivateConfirmModal(false)}
        confirmText="Yes, Reactivate"
        cancelText="No"
        isConfirming={reactivateLoading}
      />
      <ConfirmationModal
        isOpen={showActivateConfirmModal}
        title="Complete Subscription"
        message="Your subscription is incomplete. Do you want to complete the payment now?"
        onConfirm={confirmActivate}
        onCancel={() => setShowActivateConfirmModal(false)}
        confirmText="Complete Payment"
        cancelText="Later"
        isConfirming={activateLoading}
      />
    </div>
  )
}

const Info = ({ label, value, color = 'text-gray-900' }) => (
  <div>
    <p className="text-gray-600 text-sm">{label}</p>
    <p className={`font-medium ${color}`}>{value}</p>
  </div>
)

export default BusinessPlansComponent
